/**
 * Avis de valeur d'un dossier : même parcours que l'app. Tout est calculé côté
 * serveur (AvisValeurService, AvisRendu) : le site ne fabrique pas son propre
 * document, le vendeur reçoit donc le même avis quel que soit l'outil.
 */
import React, { useCallback, useEffect, useState } from 'react';

const API = '/api/estimation';
function h() { return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, 'Content-Type': 'application/json' }; }

interface Avis {
  url: string;
  urlPdf: string;
  urlApercu?: string | null;
  genereLe: string;
  envoyeLe: string | null;
  vues: number;
  aActualiser: boolean;
  options?: { atouts?: string[]; messageAgent?: string; version?: string };
}
interface Consultation { format: string; appareil: string | null; created_at: string }
interface ProfilPro {
  profilPro?: { statut?: string; rsac?: string; cartePro?: string; mentionsLibres?: string };
  telephone?: string;
  atouts?: Array<{ cle: string; libelle: string }>;
}

const LIBELLES_MANQUANTS: Record<string, string> = { cartePro: 'carte professionnelle', rsac: 'RSAC', telephone: 'téléphone' };
const dateFr = (d: string) => new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function StatistiquesAvis() {
  const [s, setS] = useState<{ total: number; envoyes: number; consultes: number; mandats: number; tauxTransformation: number | null } | null>(null);
  useEffect(() => {
    fetch(`${API}/avis/statistiques`, { headers: h() }).then(r => (r.ok ? r.json() : null)).then(setS).catch(() => { /* bandeau facultatif */ });
  }, []);
  if (!s || !s.total) return null;
  const tuiles: Array<[string, string | number]> = [
    ['Avis créés', s.total], ['Envoyés', s.envoyes], ['Lus', s.consultes], ['Mandats', s.mandats],
    ['Transformation', s.tauxTransformation != null ? `${s.tauxTransformation} %` : '—'],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
      {tuiles.map(([l, v]) => (
        <div key={l} className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-xl font-bold text-gray-900">{v}</p>
          <p className="text-xs text-gray-500">{l}</p>
        </div>
      ))}
    </div>
  );
}

interface Props { dossierId: number; clientNom?: string; clientEmail?: string; clientTelephone?: string; adresse?: string }

export default function AvisValeurCarte({ dossierId, clientNom, clientEmail, clientTelephone, adresse }: Props) {
  const [avis, setAvis] = useState<Avis | null>(null);
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [manquants, setManquants] = useState<string[]>([]);
  const [profil, setProfil] = useState<ProfilPro | null>(null);
  const [mentions, setMentions] = useState({ cartePro: '', rsac: '', telephone: '' });
  const [atouts, setAtouts] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [reglages, setReglages] = useState(false);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [copie, setCopie] = useState(false);

  const charger = useCallback(async () => {
    const r = await fetch(`${API}/dossiers/${dossierId}/avis`, { headers: h() });
    const d = await r.json();
    if (!r.ok) throw new Error(d.message);
    setAvis(d.avis || null);
    setConsultations(d.consultations || []);
    setManquants(d.manquants || []);
    setAtouts(d.avis?.options?.atouts || []);
    setMessage(d.avis?.options?.messageAgent || '');
  }, [dossierId]);

  useEffect(() => {
    setErreur(null);
    charger().catch(() => setErreur("L'avis de valeur n'a pas pu être chargé."));
    fetch(`${API}/profil-pro`, { headers: h() }).then(r => (r.ok ? r.json() : null)).then((p: ProfilPro | null) => {
      if (!p) return;
      setProfil(p);
      setMentions({ cartePro: p.profilPro?.cartePro || '', rsac: p.profilPro?.rsac || '', telephone: p.telephone || '' });
    }).catch(() => { /* les mentions se complètent aussi côté serveur */ });
  }, [charger]);

  const generer = async (actualiser: boolean) => {
    setOccupe(actualiser || !avis ? 'generation' : 'options');
    setErreur(null);
    try {
      const r = await fetch(`${API}/dossiers/${dossierId}/avis`, {
        method: 'POST', headers: h(),
        body: JSON.stringify({ actualiser, options: { atouts, messageAgent: message } }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || "Création de l'avis impossible");
      await charger();
    } catch (e: any) {
      setErreur(e.message);
    } finally {
      setOccupe(null);
    }
  };

  const enregistrerMentions = async () => {
    setOccupe('mentions');
    try {
      const r = await fetch(`${API}/profil-pro`, { method: 'PUT', headers: h(), body: JSON.stringify(mentions) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message);
      setManquants(d.manquants || []);
    } catch {
      setErreur("Les mentions n'ont pas pu être enregistrées.");
    } finally {
      setOccupe(null);
    }
  };

  const telechargerPdf = async () => {
    setOccupe('pdf');
    setErreur(null);
    try {
      const r = await fetch(`${API}/dossiers/${dossierId}/avis/pdf`, { headers: h() });
      if (!r.ok) throw new Error();
      const lien = document.createElement('a');
      lien.href = URL.createObjectURL(await r.blob());
      lien.download = `avis-de-valeur-${dossierId}.pdf`;
      lien.click();
      setTimeout(() => URL.revokeObjectURL(lien.href), 10000);
    } catch {
      setErreur('Création du PDF impossible pour le moment.');
    } finally {
      setOccupe(null);
    }
  };

  const marquerEnvoye = async (canal: string) => {
    await fetch(`${API}/dossiers/${dossierId}/avis/envoye`, { method: 'POST', headers: h(), body: JSON.stringify({ canal }) }).catch(() => null);
    charger().catch(() => null);
  };

  const texte = () => {
    const voie = (adresse || '').replace(/\s+\d{5}\s+.*$/, '').trim();
    return `Bonjour${clientNom?.trim() ? ` ${clientNom.trim()}` : ''},\n\nVoici l'avis de valeur de votre bien${voie ? ` au ${voie}` : ''} : ${avis?.url}\n\nIl se lit directement sur votre téléphone et se télécharge en PDF. Je reste à votre disposition pour en parler.`;
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(texte());
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
      marquerEnvoye('lien');
    } catch { setErreur('Copie refusée par le navigateur.'); }
  };

  const numeroWhatsapp = (clientTelephone || '').replace(/[^\d+]/g, '').replace(/^\+/, '').replace(/^0/, '33');
  const bouton = 'px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">Avis de valeur</h3>
        {avis && <span className="text-xs text-gray-500">{avis.vues > 0 ? `Lu ${avis.vues} fois` : 'Pas encore lu'}</span>}
      </div>

      {erreur && <p className="text-sm text-red-600">{erreur}</p>}

      {manquants.length > 0 && (
        <div className="rounded-xl bg-amber-50 p-3 space-y-2">
          <p className="text-sm text-amber-800">Mentions à compléter : {manquants.map(m => LIBELLES_MANQUANTS[m] || m).join(', ')}.</p>
          {manquants.map(m => (
            <input key={m} value={mentions[m as keyof typeof mentions] ?? ''} placeholder={LIBELLES_MANQUANTS[m] || m}
              onChange={e => setMentions(v => ({ ...v, [m]: e.target.value }))}
              className="w-full border border-amber-200 rounded-lg px-3 py-1.5 text-sm" />
          ))}
          <button disabled={occupe !== null} onClick={enregistrerMentions} className={bouton}>Enregistrer</button>
        </div>
      )}

      {occupe === 'generation' && <p className="text-sm text-gray-600 animate-pulse">Préparation de l'avis : cadastre, cartes et marché… (10 à 30 secondes)</p>}

      {!avis ? (
        <>
          <p className="text-sm text-gray-500">Un document de 10 pages aux couleurs de votre réseau, et un lien suivi pour le vendeur.</p>
          <button disabled={occupe !== null} onClick={() => generer(false)} className="px-4 py-2 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 disabled:opacity-50">Créer l'avis de valeur</button>
        </>
      ) : (
        <>
          {avis.aActualiser && <p className="text-sm text-amber-800">Cet avis date un peu : pensez à l'actualiser avant de relancer le vendeur.</p>}
          <div className="flex flex-wrap gap-2">
            <a href={avis.urlApercu || avis.url} target="_blank" rel="noreferrer" className={bouton}>Voir l'avis</a>
            <button disabled={occupe !== null} onClick={telechargerPdf} className={bouton}>{occupe === 'pdf' ? 'Préparation…' : 'Télécharger le PDF'}</button>
            <button disabled={occupe !== null} onClick={() => generer(true)} className={bouton}>Actualiser</button>
            <button onClick={() => setReglages(!reglages)} className={bouton}>Personnaliser</button>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 mb-2">{avis.envoyeLe ? `Envoyé le ${dateFr(avis.envoyeLe)}. Renvoyer :` : 'Envoyer au vendeur :'}</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={copier} className={bouton}>{copie ? 'Message copié' : 'Copier le message'}</button>
              <a href={`mailto:${clientEmail || ''}?subject=${encodeURIComponent('Votre avis de valeur')}&body=${encodeURIComponent(texte())}`} onClick={() => marquerEnvoye('email')} className={bouton}>Email</a>
              <a href={`https://wa.me/${numeroWhatsapp}?text=${encodeURIComponent(texte())}`} target="_blank" rel="noreferrer" onClick={() => marquerEnvoye('whatsapp')} className={bouton}>WhatsApp</a>
              <a href={`sms:${clientTelephone || ''}?&body=${encodeURIComponent(texte())}`} onClick={() => marquerEnvoye('sms')} className={bouton}>SMS</a>
            </div>
          </div>

          {consultations.length > 0 && (
            <div className="pt-2 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 mb-1">Lectures</p>
              {consultations.slice(0, 5).map((c, i) => (
                <p key={`${c.created_at}-${i}`} className="text-xs text-gray-500">{dateFr(c.created_at)} · {c.format === 'pdf' ? 'PDF' : 'page web'}</p>
              ))}
            </div>
          )}
        </>
      )}

      {reglages && (
        <div className="pt-2 border-t border-gray-100 space-y-2">
          {!!profil?.atouts?.length && (
            <div className="flex flex-wrap gap-2">
              {profil.atouts.map(a => (
                <label key={a.cle} className="flex items-center gap-1.5 text-xs text-gray-700">
                  <input type="checkbox" className="accent-violet-600" checked={atouts.includes(a.cle)}
                    onChange={e => setAtouts(l => e.target.checked ? [...l, a.cle] : l.filter(x => x !== a.cle))} />
                  {a.libelle}
                </label>
              ))}
            </div>
          )}
          <textarea value={message} maxLength={1200} rows={3} onChange={e => setMessage(e.target.value)} placeholder="Votre mot au vendeur (facultatif)"
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none" />
          <button disabled={occupe !== null} onClick={() => generer(false)} className={bouton}>Enregistrer</button>
        </div>
      )}
    </div>
  );
}
